/**
 * Phase 4 — Berechnung (ist + soll + diff + Hinweise).
 *
 * Production code path:
 *  - Worker pg-boss job 'calculate' nach state=validated
 *  - worker/src/handlers/calculate.ts: resolve_kv_for_employee + work_schedule
 *  - worker/src/calc-abrechnung.ts: computeAbrechnung() (Pause als Hinweis, keine
 *    stille Subtraktion). Persistiert card.data.calculation + kv_snapshot,
 *    state=calculated.
 *
 * Glass-Box-Pattern: liest direkt aus ctx.card + ctx.timesheet.data.calculation.
 * KEIN eigenes Card-Dropdown — Card-Selection geschieht am Werkstatt-Level
 * (URL ?card=…). Steps sind data-driven aus dem Production-Output, nicht aus
 * client-Recompute.
 *
 * Output dieser Phase = Input von Phase 5:
 *   - data.calculation (ist/soll/diff pro Tag + Wochen + Totals + Hinweise)
 *   - state = calculated
 */
import { Link } from "react-router-dom";
import type { WerkstattCardContext } from "../../pages/WerkstattPage";
import type { MonthResult, DayResult } from "../../lib/calc-abrechnung";
import { PipelineStepList, type PipelineStep } from "../SettingsTestOcr";
import { PhaseHeader, withUuids } from "./Phase1Upload";
import { AbrechnungTest } from "../AbrechnungTest";
import { useClientRuleset, type ClientRuleset } from "../../lib/client-rules";

export function Phase4Berechnung({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card;
  const isExpense = c?.kind === "expense" || c?.kind === "unknown";

  if (isExpense) {
    return (
      <div>
        <PhaseHeader
          num={4}
          title="Berechnung (Diäten-Pfad)"
          purpose="Tagessatz pro Reise-Eintrag + Pauschale + Z/M-Abzug. Komplett anderes Regelwerk als Stundenliste."
          inputs="data.entries[] (validiert) + employees.* (für Pay-Mode + KM-Sätze, geplant)"
          outputs="data.calculation = {entries[], totals: {inland_h, ausland_h, total_eur}} + state=calculated"
          prodFiles={[
            "worker/src/handlers/calculate.ts (kind-aware dispatch — geplant)",
            "worker/src/calc-expense.ts (Austrian Tagessatz-Logik, DEFAULT_RATES für 2026)",
          ]}
          userGates="Keine — vollautomatisch nach Validierung"
        />
        <ExpenseBerechnungInspector ctx={ctx} />
      </div>
    );
  }

  return (
    <div>
      <PhaseHeader
        num={4}
        title="Berechnung (Stundenliste-Pfad)"
        purpose="ist/soll/Δ pro Tag + Wochen-Aggregation + Status-Aggregat + Plausibilitäts-Hinweise"
        inputs="data.days (validiert) + employee.work_schedule + KV-Config"
        outputs="data.calculation = {days[], weeks[], totals} + state=calculated"
        prodFiles={[
          "worker/src/handlers/calculate.ts",
          "worker/src/calc-abrechnung.ts",
          "frontend/src/lib/calc-abrechnung.ts (Werkstatt-Spec, Single-Source)",
        ]}
        userGates="Keine — vollautomatisch nach Validierung"
      />
      {ctx.card ? (
        <CardBerechnungInspector ctx={ctx} />
      ) : (
        <div>
          <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
            Keine aktive Karte. Generischer Abrechnungs-Test zum Experimentieren:
          </div>
          <div className="phase-embedded-tool">
            <AbrechnungTest />
          </div>
        </div>
      )}
    </div>
  );
}

function ExpenseBerechnungInspector({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card!;
  const data = (c as any).data ?? {};
  const entries: any[] = Array.isArray(data.entries) ? data.entries : [];
  const calculation = data.calculation ?? null;
  const calcDone = !!calculation;
  const calcRunning = c.state === "validated" && !calculation;

  // Heuristic für die Doku-Anzeige (lokal — Production-Berechnung läuft im
  // worker/src/calc-expense.ts):
  const FIELDS_EX = ["tag", "datum", "kd_nr", "kunde", "zm_abzug", "m_verr", "ort", "abfahrt", "rueckkehr"];
  let totalCells = 0;
  for (const e of entries) {
    if (!e || typeof e !== "object") continue;
    for (const f of FIELDS_EX) if (e[f]) totalCells++;
  }

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Karte + Periode",
      description: "Card-ID + Monat aus URL/Kanban. Mitarbeiter über Header-OCR auto-resolved.",
      status: "done",
      detail: (
        <>
          {(c as any).month} ·{" "}
          {c.employee_id ? (ctx.employee ? `${ctx.employee.last_name}, ${ctx.employee.first_name}` : c.employee_id.slice(0, 8)) : "(unzugeordnet)"} ·{" "}
          <code>state={c.state}</code>
        </>
      ),
    },
    {
      num: 2,
      title: "Pro-Eintrag-Dauer (rueckkehr - abfahrt)",
      description:
        "Pro Reise-Eintrag aus abfahrt + rueckkehr in Stunden umrechnen. Bei abfahrt > "
        + "rueckkehr (Überschreitung Mitternacht) wird +24h addiert. Ergebnis bestimmt "
        + "ob ‚Halbtag‘ (3-11h), ‚Ganzer Tag‘ (>11h) oder ‚nicht zählbar‘ (<3h).",
      status: entries.length > 0 ? "done" : "pending",
      detail: entries.length > 0
        ? `${entries.length} Einträge × duration_h berechnet (siehe worker/src/calc-expense.ts:computeEntryDuration)`
        : "(wartet)",
    },
    {
      num: 3,
      title: "Klassifizierung Inland / Ausland (Ort-Heuristik)",
      description:
        "Aktuell Keyword-Liste österreichischer Städte (Wien/Graz/Linz/Salzburg/…) — wenn "
        + "ort einen Match enthält → country=AT, sonst country=foreign. Ausland-Tagessätze "
        + "kommen pro Land aus einer Tabelle (heute: default €35.30 für MVP).",
      status: entries.length > 0 ? "done" : "pending",
      detail: entries.length > 0
        ? `Keyword-Heuristik aktiv · Inland-/Ausland-Erkennung pro Eintrag · LAND-spezifische Sätze (geplant)`
        : "(wartet)",
      optional: true,
    },
    {
      num: 4,
      title: "Tagessatz pro Eintrag (AT-Steuergesetz)",
      description:
        "Austrian Reisekostenrecht 2026:\n"
        + "  · Inland ganzer Tag (>11h): €28.40\n"
        + "  · Inland halber Tag (3-11h): €28.40 × ⌈duration_h / 3⌉ / 4 (anteilig)\n"
        + "  · Ausland: €35.30 (Default-MVP, pro Land konfigurierbar)\n"
        + "  · Z/M-Abzug=true: Tagessatz × 2/3 (Mittagsverrechnung wurde abgezogen)\n"
        + "Quelle DEFAULT_RATES in worker/src/calc-expense.ts.",
      status: calcDone ? "done" : calcRunning ? "active" : "pending",
      detail: calcDone
        ? `${calculation?.entries?.length ?? "?"} Einträge berechnet · Σ €${calculation?.totals?.total_eur ?? "?"}`
        : calcRunning ? "(läuft)" : "(wartet)",
    },
    {
      num: 5,
      title: "Pauschal / Kilometer / Zusätze",
      description:
        "Wenn ‚kunde‘ oder ‚ort‘ KM-Information enthält, würde KM-Geld zusätzlich "
        + "berechnet (Austrian-Standard 0.42 €/km, ab 2026). Aktuell NICHT implementiert — "
        + "Diäten-MVP rechnet nur Tagessätze. Kommt in nächster Iteration.",
      status: "pending",
      detail: "(geplant — KM-Erkennung aus Freitext nötig)",
      optional: true,
    },
    {
      num: 6,
      title: "Monats-Aggregation",
      description:
        "Σ Inland-Stunden, Σ Ausland-Stunden, Σ Tagessätze EUR, Σ KM-Geld EUR, "
        + "Z/M-Abzug-Anzahl. Pro Mitarbeiter ein Monatsbeleg.",
      status: calcDone ? "done" : "pending",
      detail: calcDone && calculation?.totals
        ? `Inland ${calculation.totals.inland_h ?? "?"}h · Ausland ${calculation.totals.ausland_h ?? "?"}h · Σ €${calculation.totals.total_eur ?? "?"}`
        : "(wartet)",
    },
    {
      num: 7,
      title: "RZL-Export-Preview (Diäten-Konten)",
      description:
        "Diäten gehen auf andere RZL-Konten als Stundenliste-Werte:\n"
        + "  · Konto ‚Diäten Inland steuerfrei‘ (€28.40-Anteil)\n"
        + "  · Konto ‚Diäten Ausland steuerfrei‘ (pro Land)\n"
        + "  · Konto ‚Kilometergeld‘ (wenn Kunde KM-Erfassung hat)\n"
        + "Mapping kommt aus dem Mandanten-Regelwerk (rzl_export_labels — analog zu "
        + "Stundenliste, aber für Diäten erweitert). Heute hartcodiert für Sibit-MVP.",
      status: calcDone ? "done" : "pending",
      detail: calcDone
        ? "Preview ähnlich zu Stundenliste — Spalten siehe worker/src/calc-expense.ts → toRzlRows()"
        : "(wartet)",
      optional: true,
    },
    {
      num: 8,
      title: "Karte ab state=calculated read-only — geshared",
      description:
        "Identisch zum Stundenliste-Pfad: PATCH /api/expenses/:id lehnt Updates in "
        + "{calculated, exported, archived, superseded} mit HTTP 409 card_read_only ab. "
        + "Hub-Code ist kind-agnostic.",
      status: calcDone ? "done" : "pending",
      detail: calcDone
        ? "✓ Server-Gate aktiv: PATCH liefert 409 card_read_only in diesem State."
        : "(wartet)",
      optional: true,
    },
  ];

  return (
    <div>
      <div style={{
        background: "#fef3c7", border: "1px solid #f59e0b", padding: "8px 12px",
        borderRadius: 4, marginBottom: 12, fontSize: 13,
      }}>
        <strong>⚙️ Diäten-Berechnung (in Aufbau)</strong> — Tagessatz-Logik existiert
        bereits in <code>worker/src/calc-expense.ts</code> mit DEFAULT_RATES für 2026.
        Was noch fehlt: kind-aware Dispatch im <code>worker/src/handlers/calculate.ts</code>
        damit der Worker beim state=validated für expense-Karten <em>computeExpense</em>
        statt <em>computeTimesheet</em> aufruft, plus die Diäten-RZL-Konten im Mandant-Regelwerk.
      </div>
      <PipelineStepList steps={withUuids(c.id, "berechnung", steps)} />
      {entries.length > 0 && (
        <div className="muted" style={{ marginTop: 16, fontSize: 12 }}>
          Cell-Stats: {totalCells} Cells in {entries.length} Einträgen.
        </div>
      )}
    </div>
  );
}

function CardBerechnungInspector({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card!;
  const t = ctx.timesheet;
  const emp = c.employee_id
    ? ctx.employees.find(e => e.id === c.employee_id) ?? null
    : null;
  const schedule = emp?.work_schedule ?? null;
  const prodCalc: MonthResult | null = (t?.data as any)?.calculation ?? null;
  const kvSnapshot = (t as any)?.kv_snapshot ?? null;
  // T04: Regelwerk kommt vom Hub (dieselbe Sicht wie die Worker-Calc), nicht
  // mehr aus einer Frontend-Kopie. fail-loud statt stillem Sibit-Fallback.
  const { ruleset } = useClientRuleset(c.client_id);
  if (ruleset === undefined) {
    return <div className="muted" style={{ padding: 16 }}>Mandant-Regelwerk wird geladen…</div>;
  }
  if (ruleset === null) {
    return (
      <div style={{ padding: 16, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 13 }}>
        <strong>Kein Regelwerk für Mandant »{c.client_id}«.</strong>{" "}
        Die Berechnung stoppt für diesen Mandanten bewusst (fail-loud) — client_rules-Eintrag fehlt.
      </div>
    );
  }
  const payMode = emp?.ueberstunden_pay_mode ?? ruleset.default_ueberstunden_pay_mode;

  // Phase-Done aus DATEN, nicht aus State (per Glass-Box-Pattern):
  // calculation persistiert ⇒ Phase 4 fertig.
  const calcDone = !!prodCalc;
  const calcRunning = c.state === "validated" && !prodCalc;

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Karte + Periode (aus URL / Kanban-Link)",
      description:
        "Card-ID kommt vom Werkstatt-Level via ?card=. Mitarbeiter via Header-OCR + "
        + "Levenshtein in Phase 2 Step 5 auto-resolved. Single-Source: keine eigene "
        + "Card-Selection in dieser Phase — Drift-Antipattern (Pattern: Bienenwabe).",
      status: "done",
      detail: (
        <>
          {c.month} · {emp ? `${emp.last_name}, ${emp.first_name}` : "(unzugeordnet)"} ·
          {" "}<code>state={c.state}</code>
        </>
      ),
    },
    {
      num: 2,
      title: "Soll-Schedule des Mitarbeiters",
      description:
        "employee.work_schedule definiert pro Wochentag die Soll-Stunden. "
        + "pattern=weekly → week_a für alle Wochen. pattern=biweekly → ISO-KW alterniert (ungerade=A, gerade=B).",
      status: schedule ? "done" : emp ? "error" : "pending",
      detail: schedule
        ? scheduleDetail(schedule)
        : emp ? "⚠ Kein Schedule — Abrechnung nicht möglich" : "(wartet auf Employee-Match aus Phase 2)",
    },
    {
      num: 3,
      title: "KV-Snapshot (zum Stichtag)",
      description:
        "Worker snapshottet KV zum 1. des Monats. pause_minutes_after_6h fließt in "
        + "Hinweise (NICHT in stille Subtraktion — siehe Glass-Box-Antipattern \"stille Korrektur\").",
      status: kvSnapshot ? "done" : prodCalc ? "error" : calcRunning ? "active" : "pending",
      detail: kvSnapshot
        ? `${kvSnapshot.name} · Pflichtpause ${kvSnapshot.pause_minutes_after_6h}min nach 6h · Wochennorm ${kvSnapshot.normal_hours_per_week}h`
        : prodCalc
          ? "⚠ Kein kv_snapshot persistiert (Worker-Bug?)"
          : calcRunning ? "(läuft)" : "(wartet)",
    },
    {
      num: 4,
      title: "Pro-Tag-Berechnung (Production-Output)",
      description:
        "Worker hat ist_h, soll_h, diff_h pro Tag berechnet. Status-Codes K/U/F → ist=soll. "
        + "ZA → ist=0 (Master-System trackt Saldo). Pflichtpause/Sequenz/Tageshöchst sind HINWEISE.",
      status: prodCalc ? "done" : calcRunning ? "active" : "pending",
      detail: prodCalc
        ? renderDaySummary(prodCalc)
        : calcRunning ? "(läuft)" : `(wartet · state=${c.state})`,
    },
    {
      num: 5,
      title: "Biweekly-Mapping (ISO-8601)",
      description:
        "KW 1 = erste Woche mit Donnerstag. Konvention: ungerade KW → week_a, gerade KW → week_b.",
      status: !schedule ? "pending" : prodCalc ? "done" : "pending",
      detail: !schedule
        ? "(wartet auf Schedule)"
        : schedule.pattern === "biweekly"
          ? renderBiweeklyDetail(prodCalc)
          : "pattern=weekly — kein Mapping nötig",
      optional: schedule?.pattern !== "biweekly",
    },
    {
      num: 6,
      title: "Wochen-Aggregation (inkl. Geteilte Woche)",
      description:
        "Pro ISO-KW: Σ ist, Σ soll, Σ Δ. Master-System macht den Rest. "
        + "Geteilte Woche (eingeführt 2026-05-22): wenn 1. nicht Mo, holt der "
        + "Worker die Vormonats-Tage der ersten KW hinzu (cross_month_state="
        + "'enriched_with_prev'). Letzte KW wenn nicht So endend wird deferred "
        + "(mehrstunden + ueberstunden_50 = 0; Folgemonat übernimmt). So wird "
        + "jede KW genau einmal bewertet — keine Doppelzählung.",
      status: prodCalc?.weeks?.length ? "done" : "pending",
      detail: prodCalc?.weeks ? (
        <>
          {renderWeeks(prodCalc)}
          {(() => {
            const cms = prodCalc.cross_month_split as any;
            if (!cms) return null;
            const first = cms.first_week_state && cms.first_week_state !== "no_split";
            const last = cms.last_week_state === "deferred_to_next";
            if (!first && !last) return null;
            return (
              <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                {first && (
                  <div>
                    Erste KW{cms.first_week_iso}:{" "}
                    <code>{cms.first_week_state}</code>
                    {cms.prev_month_card_id ? (
                      <> · Vormonats-Karte <code>{cms.prev_month_card_id.slice(0, 8)}</code></>
                    ) : (
                      <> · keine Vormonats-Karte</>
                    )}
                  </div>
                )}
                {last && (
                  <div>
                    Letzte KW{cms.last_week_iso}: <code>deferred_to_next</code> (Folgemonat bewertet)
                  </div>
                )}
              </div>
            );
          })()}
        </>
      ) : "(wartet)",
    },
    {
      num: 7,
      title: "Status-Aggregat (Master-Übergabe)",
      description:
        "Anzahl K/U/F/ZA/P + konsumierte ZA-Stunden. Vortrag-Saldo trackt RZL.",
      status: prodCalc ? "done" : "pending",
      detail: prodCalc ? renderStatusAggregat(prodCalc) : "(wartet)",
    },
    {
      num: 8,
      title: "Monats-Total + Hinweise",
      description: "Summen + Hinweis-Count zur Validierung durch Birgit.",
      status: prodCalc ? "done" : "pending",
      detail: prodCalc ? renderTotals(prodCalc) : "(wartet)",
    },
    {
      num: 9,
      title: "Mandant-Regelwerk angewendet",
      description:
        `Regeln für Mandant '${ruleset.client_name}'. Quelle: GET /api/client-rules `
        + "(= loadClientRuleset, dieselbe Sicht wie die Worker-Calc; Mirror-Paar worker⇄hub). "
        + `Wochen-Schwelle ${ruleset.ueberstunden_weekly_threshold_h}h, `
        + `Schwellen-Modell '${ruleset.threshold_model}', `
        + `Mehrstunden-Routing '${ruleset.mehrstunden_routing}'.`,
      status: "done",
      detail: (
        <>
          {ruleset.rules.length} Regeln aktiv ·{" "}
          <Link to="/settings" style={{ fontWeight: 600 }}>Regelwerk in Settings →</Link>
        </>
      ),
    },
    {
      num: 10,
      title: "Mehrstunden-Klassifikation (Soll bis 42h)",
      description:
        "Pro ISO-Woche: clamp(weekly_ist - weekly_soll, 0, 42 - weekly_soll). "
        + "Mehrstunden gehen IMMER aufs ZA-Konto (Regelwerk-Regel 3).",
      status: prodCalc ? "done" : "pending",
      detail: prodCalc
        ? `Σ ${prodCalc.totals.mehrstunden_total_h}h Mehrstunden über ${prodCalc.weeks.length} Wochen`
        : "(wartet)",
    },
    {
      num: 11,
      title: "Überstunden 50% (über 42h/Woche)",
      description:
        "Pro ISO-Woche: max(0, weekly_ist - 42). 50%-Zuschlag. Routing pro MA "
        + "via employees.ueberstunden_pay_mode (cash | za).",
      status: prodCalc ? "done" : "pending",
      detail: prodCalc
        ? `Σ ${prodCalc.totals.ueberstunden_50_total_h}h über ${prodCalc.weeks.length} Wochen`
        : "(wartet)",
    },
    {
      num: 12,
      title: "Pay-Mode-Routing pro Mitarbeiter",
      description:
        `employees.ueberstunden_pay_mode = '${payMode}' für diese MA. `
        + (payMode === "pauschale" ? (
          "PAUSCHALE-Modus (eingeführt 2026-05-22 mit Go-Live): die Vertrags-"
          + "Pauschale deckt alle Mehr- und Überstunden ab. Mehrstunden + Überstunden "
          + "50% werden auf 0 gesetzt — KEINE ZA-Akkumulation, KEINE Cash-Auszahlung. "
          + "Status-Tage (K/U/F/ZA/P) und Diäten zählen NORMAL. CSV-Export: Spalten "
          + "Normalstunden, Mehrstunden_ZA, Überstunden_50_cash, Überstunden_50_za, "
          + "ZA_konsumiert sind alle 0; Status- und Diäten-Spalten regulär."
        ) : (
          `Mehrstunden → ZA-Konto (RZL-Label '${ruleset.rzl_export_labels[payMode].mehrstunden}'). `
          + `Überstunden 50% → ${payMode === "cash" ? "Cash-Auszahlung" : "ZA-Konto"} `
          + `(RZL-Label '${ruleset.rzl_export_labels[payMode].ueberstunden_50}').`
        )),
      status: prodCalc ? (emp ? "done" : "error") : "pending",
      detail: !prodCalc ? "(wartet)" : !emp ? "⚠ Kein Mitarbeiter — Routing nicht möglich" : (
        payMode === "pauschale" ? (
          <>
            Pauschale: Mehrstunden + Überstunden auf 0 gesetzt.{" "}
            Roh-Werte (für Audit): Mehrstunden <code>{prodCalc.totals.mehrstunden_total_h}h</code>,{" "}
            Überstunden 50% <code>{prodCalc.totals.ueberstunden_50_total_h}h</code> →{" "}
            Export: <strong>0h</strong>.
          </>
        ) : (
          <>
            Cash: <strong>{prodCalc.totals.ueberstunden_cash_h}h</strong> ·{" "}
            ZA-Konto: <strong>{prodCalc.totals.ueberstunden_za_h}h</strong> ·{" "}
            MA pay_mode=<code>{payMode}</code>
          </>
        )
      ),
    },
    {
      num: 13,
      title: "RZL-Export-Preview",
      description:
        "Vorschau der Zeilen die Phase 6 im RZL-Excel produzieren wird. "
        + "Spalten-Mapping kommt aus dem Mandanten-Regelwerk (rzl_export_labels) "
        + "+ employees.ueberstunden_pay_mode. Werte direkt aus data.calculation.",
      status: c.state === "exported" ? "done" : calcDone ? "done" : "pending",
      detail: !calcDone ? "(wartet auf Berechnung)" : (
        <RzlExportPreview prodCalc={prodCalc!} payMode={payMode} ruleset={ruleset} emp={emp} />
      ),
      optional: c.state !== "exported",
    },
    {
      num: 14,
      title: "Karte ab state=calculated read-only",
      description:
        "Sobald die Karte berechnet ist, dürfen Felder (data.days, employee_id) "
        + "nicht mehr verändert werden — sonst geht die Berechnung gegen veraltete "
        + "Werte und Birgit verliert die Reproduzierbarkeit. "
        + "Server-Gate: PATCH /api/timesheets/:id (hub/src/routes/timesheets.ts) "
        + "lehnt Updates in states {calculated, exported, archived, superseded} "
        + "mit HTTP 409 card_read_only ab (seit 2026-05-18).",
      status: calcDone ? "done" : "pending",
      detail: !calcDone
        ? "(wartet auf state=calculated)"
        : "✓ Server-Gate aktiv: PATCH liefert 409 card_read_only in diesem State.",
      optional: true,
    },
    {
      num: 15,
      title: "Stale-Detection: Settings vs Snapshot",
      description:
        "Worker speichert beim Calc einen Snapshot der relevanten Inputs in "
        + "data.calculation.inputs = {work_schedule, ueberstunden_pay_mode, kv_id}. "
        + "Hub vergleicht beim GET /api/timesheets/:id den Snapshot mit den aktuellen "
        + "employees-Werten — wenn divergent (Settings nachträglich geändert) → "
        + "calc_stale=true. UI zeigt im Detail-View einen Amber-Banner mit "
        + "Hinweis 'Berechnung veraltet, Recalc empfohlen'. Verhindert dass RZL-Export "
        + "alte Soll-Stunden o.ä. exportiert nach einer Settings-Änderung. "
        + "Sources: worker/src/handlers/calculate.ts (Snapshot), "
        + "hub/src/routes/timesheets.ts r.get/:id (Vergleich), "
        + "frontend/src/pages/TimesheetDetailPage.tsx (Banner).",
      status: calcDone ? "done" : "pending",
      detail: !calcDone
        ? "(wartet auf state=calculated)"
        : (c as any)?.calc_stale
          ? "⚠ Karte ist STALE — Settings haben sich nach der Berechnung geändert. Banner ist sichtbar im Detail-View."
          : "✓ Snapshot stimmt mit aktuellem employee-Setting überein.",
      optional: true,
    },
    {
      // Tages-Tabelle als regulärer Glass-Box-Step (2026-05-22): die volle
      // Production-Output-Tabelle ist das sichtbare Endergebnis der Berechnung
      // — sie gehört in die nummerierte Pipeline, nicht als anonymer Anhang
      // unter der Liste. Damit hat die Tabelle eine stabile UUID die in
      // Diskussionen referenziert werden kann (stepUuid → Step-Chip).
      num: 16,
      title: "Tages-Tabelle (Production-Output)",
      description:
        "Vollständige Tag-für-Tag-Sicht: Ist/Soll/Δ pro Tag, Status-Code, "
        + "Hinweise inline. Daten kommen direkt aus data.calculation.days. "
        + "Optisch identisch im Detail-View (TimesheetDetailPage) damit "
        + "Birgit und Audit denselben Aufbau sehen.",
      status: prodCalc ? "done" : "pending",
      detail: prodCalc
        ? <DayTable days={prodCalc.days ?? []} />
        : "(wartet auf Berechnung)",
    },
  ];

  return (
    <>
      <PipelineStepList steps={withUuids(c.id, "berechnung", steps)} />
    </>
  );
}

// ─── Renderers (kopiert/extrahiert aus AbrechnungTest — Single-Source-Risk
//                bewusst: bei der nächsten Iteration in eine lib/ verschieben). ─

function scheduleDetail(s: any): string {
  if (!s) return "—";
  const pattern = s.pattern ?? "weekly";
  const a = s.week_a ?? {};
  const aStr = `Mo ${a.mo ?? 0} · Di ${a.di ?? 0} · Mi ${a.mi ?? 0} · Do ${a.do ?? 0} · Fr ${a.fr ?? 0} · Sa ${a.sa ?? 0}`;
  if (pattern === "biweekly" && s.week_b) {
    const b = s.week_b;
    const bStr = `Mo ${b.mo ?? 0} · Di ${b.di ?? 0} · Mi ${b.mi ?? 0} · Do ${b.do ?? 0} · Fr ${b.fr ?? 0} · Sa ${b.sa ?? 0}`;
    return `${pattern} · A: ${aStr} (Σ ${s.soll_h_woche_a}h) · B: ${bStr} (Σ ${s.soll_h_woche_b}h)`;
  }
  return `${pattern} · ${aStr} · Σ ${s.soll_h_woche_a ?? "?"}h/Woche${s.notes ? " · " + s.notes : ""}`;
}

function renderDaySummary(r: MonthResult): string {
  const filled = (r.days ?? []).filter(d => d.ist_h > 0 || d.status).length;
  const hints = (r.days ?? []).reduce((s, d) => s + d.hints.length, 0);
  return `${filled}/${r.days?.length ?? 0} Tage befüllt · Σ ist ${r.totals.stunden_ist}h · Σ soll ${r.totals.stunden_soll}h · ${hints} Hinweise`;
}

function renderBiweeklyDetail(r: MonthResult | null): string {
  if (!r) return "(wartet)";
  const byWeek = new Map<number, { woche: "a" | "b"; days: number }>();
  for (const d of r.days ?? []) {
    const entry = byWeek.get(d.iso_week) ?? { woche: d.woche, days: 0 };
    entry.days++;
    byWeek.set(d.iso_week, entry);
  }
  return [...byWeek.entries()].sort(([a], [b]) => a - b)
    .map(([wk, { woche, days }]) => `KW${wk}=${woche} (${days}d)`).join(" · ");
}

function renderWeeks(r: MonthResult): string {
  return (r.weeks ?? []).map(w => `KW${w.iso_week}: ist ${w.ist_h_sum}h soll ${w.soll_h_sum}h Δ ${formatSigned(w.diff_h_sum)}h`).join(" · ");
}

function renderStatusAggregat(r: MonthResult): string {
  const t = r.totals;
  return `K ${t.tage_K}d · U ${t.tage_U}d · F ${t.tage_F}d · ZA ${t.tage_ZA}d (${t.stunden_ZA_konsumiert}h) · P ${t.tage_P}d`;
}

function renderTotals(r: MonthResult): string {
  const t = r.totals;
  return `Σ ist ${t.stunden_ist}h · Σ soll ${t.stunden_soll}h · Δ ${formatSigned(t.diff_h)}h · ${t.hints_count} Hinweise`;
}

function formatSigned(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

/** Tages-Tabelle. Genutzt vom Werkstatt-Step 16 UND vom TimesheetDetailPage —
 *  identisch in beiden Views damit Birgit + Audit denselben Aufbau sehen.
 *
 *  Hinweise-Spalte (iteriert 2026-05-22): vorher waren nur Counts ("🔴 2 🟡 3")
 *  mit Hover-Tooltip — auf Touch nicht aufrufbar, ohne Hover blind. Jetzt
 *  inline Hint-Text mit farbigem Chip pro Hinweis; ab dem dritten "+N more".
 *  Politik: Wenn man die Hinweise nicht sieht, sind sie wertlos. */
export function DayTable({ days }: { days: DayResult[] }) {
  return (
    <table className="day-table" style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
          <th>Tag</th><th>Datum</th><th>KW</th><th>W</th><th>Status</th>
          <th style={{ textAlign: "right" }}>Ist</th>
          <th style={{ textAlign: "right" }}>Soll</th>
          <th style={{ textAlign: "right" }}>Δ</th>
          <th>Hinweise</th>
        </tr>
      </thead>
      <tbody>
        {days.map(d => {
          const hintsRed = d.hints.filter(h => h.level === "red");
          const hintsYellow = d.hints.filter(h => h.level === "yellow");
          const bgRed = hintsRed.length > 0 ? "rgba(239,68,68,0.08)" : undefined;
          const bgYellow = !bgRed && hintsYellow.length > 0 ? "rgba(245,158,11,0.08)" : undefined;
          return (
            <tr key={d.day} style={{ borderBottom: "1px solid #eee", background: bgRed ?? bgYellow, verticalAlign: "top" }}>
              <td>{d.day}</td>
              <td>{d.date}</td>
              <td>KW{d.iso_week}</td>
              <td>{d.woche.toUpperCase()}</td>
              <td>{d.status ?? ""}</td>
              <td style={{ textAlign: "right" }}>{d.ist_h.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{d.soll_h.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{formatSigned(+d.diff_h.toFixed(2))}</td>
              <td><HintCell hints={d.hints} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Render bis zu 2 Hinweise inline mit farb-codiertem Chip + Text, danach
 *  "+N weitere" als <details>-Summary die expandable ist. Auf Touch + ohne
 *  Hover lesbar. Politik: Birgit muss ohne Aktion sehen WAS los ist. */
function HintCell({ hints }: { hints: Array<{ level: string; text: string }> }) {
  if (!hints || hints.length === 0) return <span style={{ color: "#9ca3af" }}>—</span>;
  const INLINE_LIMIT = 2;
  const inline = hints.slice(0, INLINE_LIMIT);
  const rest = hints.slice(INLINE_LIMIT);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {inline.map((h, i) => <HintChip key={i} level={h.level} text={h.text} />)}
      {rest.length > 0 && (
        <details style={{ fontSize: 12 }}>
          <summary style={{ cursor: "pointer", color: "#6b7280" }}>
            +{rest.length} weitere
          </summary>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
            {rest.map((h, i) => <HintChip key={i} level={h.level} text={h.text} />)}
          </div>
        </details>
      )}
    </div>
  );
}

function HintChip({ level, text }: { level: string; text: string }) {
  const styles = level === "red"
    ? { dot: "#dc2626", color: "#991b1b", bg: "#fef2f2", border: "#fecaca" }
    : level === "yellow"
      ? { dot: "#d97706", color: "#92400e", bg: "#fffbeb", border: "#fde68a" }
      : { dot: "#6b7280", color: "#374151", bg: "#f9fafb", border: "#e5e7eb" };
  return (
    <div style={{
      display: "inline-flex", alignItems: "flex-start", gap: 6,
      padding: "2px 6px", borderRadius: 3, fontSize: 12,
      background: styles.bg, border: `1px solid ${styles.border}`, color: styles.color,
      lineHeight: 1.35,
    }}>
      <span aria-hidden style={{
        width: 6, height: 6, borderRadius: "50%", background: styles.dot,
        marginTop: 5, flex: "0 0 auto",
      }} />
      <span>{text}</span>
    </div>
  );
}

/**
 * Mini-Tabelle die zeigt, was Phase 6 ins RZL-Excel schreiben wird.
 * Quelle der Spalten: ruleset.rzl_export_labels[payMode], Werte aus prodCalc.totals.
 */
function RzlExportPreview({
  prodCalc, payMode, ruleset, emp,
}: {
  prodCalc: MonthResult;
  payMode: "cash" | "za" | "pauschale";
  ruleset: ClientRuleset;
  emp: { last_name: string; first_name: string; dienstnehmernummer: string } | null;
}) {
  const isPauschale = payMode === "pauschale";
  // Pauschale hat keine eigenen rzl_export_labels — fallback auf "za"-Labels
  // damit Tabellen-Spaltennamen stabil bleiben; Werte sind eh 0.
  const labels = ruleset.rzl_export_labels[isPauschale ? "za" : payMode];
  const t = prodCalc.totals;
  const empLabel = emp
    ? `${emp.last_name}, ${emp.first_name} (${emp.dienstnehmernummer})`
    : "(unzugeordnet)";

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
        Mitarbeiter: <strong>{empLabel}</strong> · Pay-Mode: <code>{payMode}</code>
        {isPauschale && (
          <span style={{ color: "#92400e", marginLeft: 8 }}>
            ⚠ Pauschale: Stunden-Spalten alle 0 (Vertragspauschale)
          </span>
        )}
      </div>
      <table style={{
        width: "100%", borderCollapse: "collapse", fontSize: 12,
        background: "white", border: "1px solid #e5e7eb",
      }}>
        <thead>
          <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
            <th style={cellStyle}>RZL-Spalte</th>
            <th style={cellStyle}>Stunden</th>
            <th style={cellStyle}>Quelle</th>
          </tr>
        </thead>
        <tbody>
          <tr style={rowStyle}>
            <td style={cellStyle}>Normalstunden</td>
            <td style={cellStyle}><strong>{isPauschale ? 0 : t.stunden_ist}</strong> h</td>
            <td style={{ ...cellStyle, color: "#6b7280" }}>
              {isPauschale
                ? <>Pauschale: 0 (roh: {t.stunden_ist}h)</>
                : "data.calculation.totals.stunden_ist"}
            </td>
          </tr>
          <tr style={rowStyle}>
            <td style={cellStyle}>{labels.mehrstunden}</td>
            <td style={cellStyle}><strong>{isPauschale ? 0 : t.mehrstunden_total_h}</strong> h</td>
            <td style={{ ...cellStyle, color: "#6b7280" }}>
              {isPauschale
                ? <>Pauschale: 0 (roh: {t.mehrstunden_total_h}h)</>
                : "mehrstunden_total_h → immer ZA"}
            </td>
          </tr>
          {isPauschale ? null : payMode === "cash" ? (
            <tr style={rowStyle}>
              <td style={cellStyle}>{labels.ueberstunden_50}</td>
              <td style={cellStyle}><strong>{t.ueberstunden_cash_h}</strong> h</td>
              <td style={{ ...cellStyle, color: "#6b7280" }}>ueberstunden_cash_h (Auszahlung)</td>
            </tr>
          ) : (
            t.ueberstunden_50_total_h > 0 && (
              <tr style={rowStyle}>
                <td style={cellStyle}>{labels.ueberstunden_50}</td>
                <td style={cellStyle}><strong>{t.ueberstunden_50_total_h}</strong> h</td>
                <td style={{ ...cellStyle, color: "#6b7280" }}>ueberstunden_za_h zusätzlich aufs ZA-Konto</td>
              </tr>
            )
          )}
          <tr style={rowStyle}>
            <td style={cellStyle}>K / U / F (Tage)</td>
            <td style={cellStyle}>{t.tage_K} / {t.tage_U} / {t.tage_F}</td>
            <td style={{ ...cellStyle, color: "#6b7280" }}>Status-Counter (auch bei Pauschale)</td>
          </tr>
          <tr style={rowStyle}>
            <td style={cellStyle}>ZA konsumiert</td>
            <td style={cellStyle}>{isPauschale ? 0 : t.stunden_ZA_konsumiert} h</td>
            <td style={{ ...cellStyle, color: "#6b7280" }}>
              {isPauschale
                ? <>Pauschale: 0 (roh: {t.stunden_ZA_konsumiert}h)</>
                : "stunden_ZA_konsumiert"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const cellStyle: React.CSSProperties = { padding: "4px 8px", textAlign: "left", verticalAlign: "top" };
const rowStyle: React.CSSProperties = { borderBottom: "1px solid #f3f4f6" };
